/**
 * Story `fe-be-verzekeraar-gebonden-hersteller-chrome-tabs` (EPIC-019 S-C,
 * **beslissing C1**, 2026-08-21) — de route-prefixen die voor een
 * verzekeraar-gebonden (beperkte) hersteller **niet bestaan**.
 *
 * ## Waarom dit bestaat
 * Menu-items verbergen is niet genoeg. De TA-review vond vier gerenderde,
 * klikbare ingangen die **buiten** `apps.config.ts` om een route kiezen:
 * het bel-menu (`BellButton`, `deepLinkPath` komt onvalideerd van de BE — het
 * type `invoice_export_done` landt op `/facturatie/export`), het logo
 * (`Logo` → `findActiveApp(pathname).defaultRoute` brengt "home" juist *dieper*
 * de verborgen app in), de bookmark-redirects in `routes.tsx`, en de
 * credit-doorkliklinks op het dossier. Beslissing C1: **niet** per plek
 * oplossen, maar één guard + één padclassificatie.
 *
 * ## Eén bron van waarheid
 * De prefix-lijst wordt **afgeleid** uit `APPS` (`hiddenWhenRestrictedRepairer`
 * / `requiresScratchpadWhenRestricted`), niet handmatig bijgehouden. Een tweede,
 * handgeschreven padlijst zou bij een B1-wijziging stil uit de pas lopen.
 *
 * ## Wat dit WEL en NIET is
 * Dit stopt **accidentele** toegang (bookmark, bel-link, credit-link, logo) — bij
 * deze doelgroep het overgrote deel van het werkelijke risico. Het stopt **geen**
 * script: de echte handhaving blijft de BE-poorten (intake-poort in deze story;
 * planbord / order-list / kladblok / factuur-create in S-D/S-F/S-G). Daarom
 * blijft de omgevings-schakelaar op productie `false` tot S-F en S-G live zijn
 * (beslissing C5).
 *
 * Puur en `unknown`-tolerant; geen React-import, geen side-effects.
 */
import type { WhoAmIResponse } from '@carculate/api-client';

import { APPS, type App } from '@/config/apps.config';

import { isAppHiddenByMenu, isTabHiddenByMenu } from './menuVisibility';
import { isRestrictedRepairerActive, mayUseScratchpad } from './restrictedRepairer';
import { VERZEKERAAR_ROLE, isInsurerUser } from './roles';
import { canUseCourses } from './trainingMode';

/**
 * Prefix-match met dezelfde semantiek als `InsurerRouteGuard`: een prefix matcht
 * het pad **exact** of als volledig pad-segment (`p + '/'`). Zo matcht
 * `/facturatie` **niet** op `/facturatiex` — een substring-match zou een
 * onbedoelde route blokkeren.
 */
function matchesPrefix(path: string, prefix: string): boolean {
    return path === prefix || path.startsWith(prefix + '/');
}

/**
 * Normaliseer een navigatie-doel naar het pure pad: query en hash eraf, en een
 * trailing slash weg (behalve de root). Een `deepLinkPath` van de BE mag beide
 * bevatten en moet toch correct geclassificeerd worden.
 */
function normalizePath(target: unknown): string | null {
    if (typeof target !== 'string') return null;
    const raw = target.trim();
    if (raw.length === 0) return null;
    // Absolute URL's / protocol-relatieve doelen zijn geen interne route.
    if (raw.includes('://') || raw.startsWith('//')) return null;
    if (!raw.startsWith('/')) return null;
    const withoutQuery = raw.split(/[?#]/)[0] ?? '';
    if (withoutQuery.length > 1 && withoutQuery.endsWith('/')) {
        return withoutQuery.slice(0, -1);
    }
    return withoutQuery;
}

/**
 * Het eerste pad-segment (`/facturatie/dashboard` → `/facturatie`). Alleen
 * gebruikt voor **verborgen apps**: daar bestaat geen enkele zichtbare route
 * onder dat segment, dus over-blokkeren kan niet.
 */
function firstSegment(target: unknown): string | null {
    const path = normalizePath(target);
    if (path === null || path === '/') return null;
    const parts = path.split('/');
    return parts.length > 2 ? '/' + parts[1] : path;
}

/**
 * Alle prefixen van één verborgen app: de concrete tab-paden ÉN de app-root (het eerste
 * pad-segment). Zonder de root glipt een catch-all/kaal app-pad (`/facturatie`, `/inkoop`) er
 * langs, en juist dáár landt een bookmark of een BE-deeplink nog wel eens. Gedeeld door de twee
 * redenen om een app te verbergen (gebonden hersteller én uitgezet in Systeem → Menu, story
 * `be-fe-menu-aan-uit` AC 5), zodat er één padclassificatie is en geen tweede padlijst.
 */
function addAppPrefixes(app: App, add: (p: string | null | undefined) => void): void {
    // Story be-fe-support-app-behandelen (S2): een app die zijn eerste padsegment deelt met routes buiten de app
    // (Support: `/support/inbox` naast `/support/requests`) blokkeert alleen zijn eigen paden.
    const shared = app.sharedFirstSegment === true;
    add(app.defaultRoute);
    if (!shared) add(firstSegment(app.defaultRoute));
    for (const tab of app.tabs) {
        add(tab.to);
        if (!shared) add(firstSegment(tab.to));
    }
}

function prefixCollector(): { prefixes: string[]; add: (p: string | null | undefined) => void } {
    const prefixes: string[] = [];
    const add = (p: string | null | undefined) => {
        const norm = normalizePath(p);
        if (norm && !prefixes.includes(norm)) prefixes.push(norm);
    };
    return { prefixes, add };
}

/**
 * De route-prefixen die voor een **beperkte** hersteller niet bestaan, afgeleid
 * uit de declaratieve config.
 *
 * @param scratchpad `mayUseScratchpad(user)` — bij `false` komt het **losse**
 *   kladblok (`/calculatie`) er ook bij. De Calculatie-**tab** in het dossier
 *   zit niet in deze lijst (die blijft altijd, AC 15).
 */
export function hiddenPrefixesForRestrictedRepairer(scratchpad: boolean): string[] {
    const { prefixes, add } = prefixCollector();

    for (const app of APPS) {
        if (app.hiddenWhenRestrictedRepairer) {
            addAppPrefixes(app, add);
            continue;
        }
        // Een zichtbare app kan losse verborgen tabs hebben (Glas → Planbord).
        for (const tab of app.tabs) {
            if (tab.hiddenWhenRestrictedRepairer) add(tab.to);
            if (tab.requiresScratchpadWhenRestricted && !scratchpad) add(tab.to);
        }
    }

    return prefixes;
}

/**
 * Of `pathname` binnen een voor de beperkte hersteller verborgen app/route valt.
 * Zegt **niets** over of de gebruiker beperkt is — dat is
 * {@link isNavTargetBlockedForUser}.
 */
export function isPathHiddenForRestrictedRepairer(
    pathname: unknown,
    scratchpad: boolean,
): boolean {
    const path = normalizePath(pathname);
    if (path === null) return false;
    return hiddenPrefixesForRestrictedRepairer(scratchpad).some((p) =>
        matchesPrefix(path, p),
    );
}

/**
 * De enige vraag die consumers stellen: "mag DEZE gebruiker naar DIT pad?".
 * `true` = geblokkeerd (niet renderen als `<Link>`, of redirecten).
 *
 * Een niet-beperkte gebruiker krijgt **altijd** `false` — er verandert niets voor
 * hem, op welke instantie dan ook. Een niet-pad-achtig doel (`null`, leeg, externe
 * URL) is nooit geblokkeerd: dan blijft het bestaande gedrag gelden.
 */
export function isNavTargetBlockedForUser(user: unknown, target: unknown): boolean {
    // Twee onafhankelijke redenen; de ene schakelt de andere niet uit (be-fe-menu-aan-uit, AC 5).
    if (isRestrictedRepairerActive(user) && isPathHiddenForRestrictedRepairer(target, mayUseScratchpad(user))) {
        return true;
    }
    return isPathHiddenByMenu(user, target);
}

// ══════════════════════════════════════════════════════════════════════════
// Story be-fe-menu-aan-uit (AC 4/5) — apps en tabs die System in Systeem → Menu
// heeft uitgezet. DEZELFDE classificatie (afgeleid uit APPS), een tweede reden.
// ══════════════════════════════════════════════════════════════════════════

/**
 * Hoort deze app bij de wereld van deze gebruiker? Een verzekeraar ziet alleen haar eigen apps (rol
 * `verzekeraar`), iedereen anders juist niet. Zo kan een menu-sleutel van de ene wereld nooit een pad van de
 * andere blokkeren (bv. "Glas" uit voor verzekeraars mag haar dossier-inzage op `/dossiers/{id}` niet raken).
 */
function appInAudience(app: App, user: unknown): boolean {
    const insurerApp = app.requiresAnyRole?.includes(VERZEKERAAR_ROLE) === true;
    return isInsurerUser(user as WhoAmIResponse | null | undefined) ? insurerApp : !insurerApp;
}

function longestMatch(path: string, prefixes: readonly string[]): number {
    let best = -1;
    for (const p of prefixes) if (p.length > best && matchesPrefix(path, p)) best = p.length;
    return best;
}

/**
 * Valt `pathname` onder een app of tab die voor deze gebruiker uit staat? Een uitgezette app blokkeert al zijn
 * paden (tabs én het eerste pad-segment), een uitgezette tab alleen zijn eigen pad. Wint een zichtbare tab met een
 * langer of even lang pad (bv. `/te-beoordelen/dossiers` onder een uitgezette `/te-beoordelen`, of hetzelfde
 * pad in twee apps), dan is het pad niet geblokkeerd.
 */
export function isPathHiddenByMenu(user: unknown, pathname: unknown): boolean {
    const path = normalizePath(pathname);
    if (path === null) return false;
    const hidden = prefixCollector();
    const visible = prefixCollector();
    for (const app of APPS) {
        if (!appInAudience(app, user)) continue;
        if (isAppHiddenByMenu(app, user)) {
            addAppPrefixes(app, hidden.add);
            continue;
        }
        for (const tab of app.tabs) {
            if (isTabHiddenByMenu(app, tab, user)) hidden.add(tab.to);
            else visible.add(tab.to);
        }
    }
    if (hidden.prefixes.length === 0) return false;
    const h = longestMatch(path, hidden.prefixes);
    return h >= 0 && h > longestMatch(path, visible.prefixes);
}

/**
 * Waar deze app voor deze gebruiker opent (tegel in de app-wisselaar en het logo). Normaal de `defaultRoute`; staat
 * die uit (bv. Inrichting met de tab Fotoprotocollen uit in Systeem → Menu), dan het eerste menu-item van de app dat
 * niet geblokkeerd is — bij voorkeur een zonder extra rol-/contextpoort, zodat de gebruiker op een pagina landt die
 * hij ook echt ziet. `null` = er is niets bereikbaar in deze app (dan hoort de tegel er niet te staan).
 */
export function appEntryRoute(app: App, user: unknown): string | null {
    if (!app.defaultRoute) return null;
    // Story be-fe-systeem-gebruikers (P-2): een app met de OF-poort "System of canManageUsers" opent voor wie geen System
    // is op de eerste tab die hij wél mag zien (het keten-HK ⇒ `/system/users`), niet op de System-only standaardroute.
    if (app.requiresSystemContextOrCanManageUsers === true && !isSystemContext(user)) {
        const allowed = app.tabs.find(
            (t) =>
                t.enabled &&
                !t.requiresSystemContext &&
                (!t.requiresCanManageUsers || (user as WhoAmIResponse | null | undefined)?.canManageUsers === true) &&
                !isNavTargetBlockedForUser(user, t.to),
        );
        return allowed?.to ?? null;
    }
    if (!isNavTargetBlockedForUser(user, app.defaultRoute)) return app.defaultRoute;
    const open = app.tabs.filter((t) => t.enabled && !isNavTargetBlockedForUser(user, t.to));
    const ungated = open.find(
        (t) =>
            !t.requiresAnyRole &&
            !t.requiresSystemContext &&
            !t.requiresRepairerContext &&
            !t.requiresCanManageRepairers &&
            !t.requiresCanHandleSupport &&
            !t.requiresCanManageUsers &&
            !t.requiresCanManageDealerSurcharge &&
            !t.requiresBillingRunsAccess &&
            !t.requiresIntakesEnabled,
    );
    return (ungated ?? open[0])?.to ?? null;
}

/** Is de gebruiker een System-gebruiker (`userContextType === 'System'`)? */
function isSystemContext(user: unknown): boolean {
    return (user as WhoAmIResponse | null | undefined)?.userContextType === 'System';
}

/** Een route die altijd bestaat en in geen enkele app hangt (ook toegestaan voor de verzekeraar). */
const LAST_RESORT_ROUTE = '/mijn-gegevens/identiteit';

/**
 * De route waar een gebruiker naartoe gaat wanneer hij op een geblokkeerde route landt (gebonden hersteller of
 * uitgezet in Systeem → Menu): de `defaultRoute` van de **eerste zichtbare** app van zijn wereld — in de praktijk
 * het Glas-dashboard (`/dashboard`), voor een verzekeraar "Te beoordelen". Staat die route zelf uit, dan de eerste
 * zichtbare tab van die app, anders de volgende app. Afgeleid, niet hardcoded. Is niets zichtbaar, dan
 * "Mijn gegevens" — nooit een route die zelf weer geblokkeerd is (anders een redirect-lus).
 */
export function restrictedRepairerFallbackRoute(user?: unknown): string {
    for (const app of APPS) {
        if (!app.enabled || app.hiddenWhenRestrictedRepairer || !app.defaultRoute) continue;
        if (app.requiresSystemContext || app.requiresSystemContextOrAnyRole || app.requiresSystemContextOrCanManageUsers) continue;
        if (app.requiresCourses && !canUseCourses(user as WhoAmIResponse | null | undefined)) continue;
        if (app.requiresCanHandleSupport && (user as WhoAmIResponse | null | undefined)?.canHandleSupport !== true) continue;
        if (user !== undefined && !appInAudience(app, user)) continue;
        if (user === undefined && app.requiresAnyRole) continue;
        if (!isNavTargetBlockedForUser(user, app.defaultRoute)) return app.defaultRoute;
        const tab = app.tabs.find((t) => t.enabled && !isNavTargetBlockedForUser(user, t.to));
        if (tab) return tab.to;
    }
    return LAST_RESORT_ROUTE;
}
